// Collection picker for the /build page. Lists the user's collections and
// returns the selected collection_id (or null) via a controlled value/onChange
// pair. Always renders the "Standard" pill — the full Standard-legal pool is
// the supported default, so the picker stays visible even when the user has
// no uploaded collections, with an "Upload a collection" entry inside the
// menu as the path to add one.
//
// Backed by react-query so the menu stays fresh after the user adds a
// collection on /collections in another tab.
//
// Implementation note: a native <select> sizes its visible width to the
// WIDEST option, so a one-word default ("Standard") would sit in a box
// sized for "Pool: Strixhaven Sealed #2 · 84 unique". This component
// rolls its own button + popover menu so the trigger pill hugs only the
// currently-selected label — matching the segmented Exemplars /
// Discovery / Model pills on the same row.

import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Library } from "lucide-react";
import { Link } from "react-router-dom";
import { listCollections } from "../lib/api";
import { cn } from "../lib/cn";

interface Props {
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}

export default function CollectionPicker({ value, onChange, disabled }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["collections"],
    queryFn: listCollections,
  });
  const collections = data?.collections ?? [];
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click and on Escape so the menu behaves like a
  // proper popover without us needing Radix or a portal.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (isLoading) return null;

  const selected = collections.find((c) => c.id === value) ?? null;
  const label = selected ? selected.name : "Standard";

  return (
    <div className="flex items-center gap-1.5">
      <label className="text-fg-muted flex items-center gap-1.5">
        <Library size={12} />
        Collection
      </label>
      <div ref={wrapperRef} className="relative">
        {/* Trigger pill — same h-7 + bg-accent/20 + text-accent that the
            segmented controls use for their selected pill, but as a real
            <button> so its width hugs only the current label. */}
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          title={
            selected
              ? `${selected.oracle_count} unique cards · ${selected.card_count} total`
              : "Build from the full Standard-legal pool"
          }
          className={cn(
            // Match the segmented Exemplars/Discovery/Model pills
            // exactly: px-2.5 py-1 text-xs inside a rounded-md border.
            // No h-7 — let py-1 size the height the same way the
            // segmented buttons do.
            "inline-flex items-center gap-1 rounded-md border border-border",
            "px-2.5 py-1 text-xs transition-colors capitalize",
            "bg-accent/20 text-accent hover:bg-accent/30",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          <span className="truncate max-w-[140px]">{label}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="9"
            height="9"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {open && (
          <div
            role="listbox"
            className={cn(
              "absolute left-0 top-full mt-1 z-30",
              "min-w-[12rem] max-w-[18rem]",
              "rounded-md border border-border bg-bg-raised shadow-lg overflow-hidden",
            )}
          >
            <MenuItem
              selected={value === null}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              label="Standard"
              hint="full Standard-legal pool"
            />
            {collections.length > 0 && (
              <>
                <div className="border-t border-border/60" />
                {collections.map((c) => (
                  <MenuItem
                    key={c.id}
                    selected={value === c.id}
                    onClick={() => {
                      onChange(c.id);
                      setOpen(false);
                    }}
                    label={c.name}
                    hint={`${c.oracle_count} unique · ${c.card_count} total`}
                  />
                ))}
              </>
            )}
            <div className="border-t border-border/60" />
            <Link
              to="/collections"
              onClick={() => setOpen(false)}
              role="option"
              className={cn(
                "block w-full text-left px-2.5 py-1.5 text-xs transition-colors",
                "text-fg-subtle hover:bg-bg-base hover:text-fg",
              )}
            >
              <span className="truncate">Upload a collection…</span>
              <span className="block text-[10px] text-fg-subtle truncate">
                build from your binder
              </span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function MenuItem({
  label,
  hint,
  selected,
  onClick,
}: {
  label: string;
  hint: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className={cn(
        "w-full text-left px-2.5 py-1.5 text-xs transition-colors",
        "flex flex-col gap-0.5",
        selected
          ? "bg-accent/15 text-accent"
          : "text-fg hover:bg-bg-base",
      )}
    >
      <span className="truncate">{label}</span>
      <span className="text-[10px] text-fg-subtle truncate">{hint}</span>
    </button>
  );
}
